import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTasksByStatus } from '../../hooks/useTasks';
import { PlannerSidebarItem } from './PlannerSidebarItem';
import type { Task } from '../../types';

interface PlannerSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function PlannerSidebar({ isOpen, onToggle }: PlannerSidebarProps) {
  const tasks = useTasksByStatus('track');
  // Separate into: project-type parents, regular task parents, and subtasks
  const { projectTasks, regularTasks, subtasksByParent } = useMemo(() => {
    const projectItems: Task[] = [];
    const regularItems: Task[] = [];
    const subs = new Map<string, Task[]>();

    for (const t of tasks) {
      if (t.parentId) {
        const list = subs.get(t.parentId) ?? [];
        list.push(t);
        subs.set(t.parentId, list);
      } else if (t.itemType === 'project') {
        projectItems.push(t);
      } else {
        regularItems.push(t);
      }
    }

    return { projectTasks: projectItems, regularTasks: regularItems, subtasksByParent: subs };
  }, [tasks]);

  // Sort projects alphabetically, regular tasks by priority
  const sortedProjects = useMemo(() => {
    return [...projectTasks].sort((a, b) => a.title.localeCompare(b.title));
  }, [projectTasks]);

  const sortedRegularTasks = useMemo(() => {
    const priorityOrder: Record<string, number> = { p1: 0, p2: 1, p3: 2 };
    return [...regularTasks].sort((a, b) => (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2));
  }, [regularTasks]);

  // Closed state: thin strip with toggle
  if (!isOpen) {
    return (
      <div className="w-9 border-r border-border-subtle/50 flex flex-col items-center pt-3 shrink-0">
        <button
          onClick={onToggle}
          className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-bg-raised text-text-muted hover:text-text-primary transition-colors"
          aria-label="Open task sidebar"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    );
  }

  const hasContent = sortedProjects.length > 0 || sortedRegularTasks.length > 0;

  // Open state
  return (
    <div className="w-[250px] border-r border-border-subtle flex flex-col shrink-0 bg-bg-base">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
        <span className="text-[13px] font-medium text-text-primary">Tasks</span>
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-bg-raised text-text-muted"
          aria-label="Close task sidebar"
        >
          <ChevronLeft size={14} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto py-1" style={{ overscrollBehavior: 'contain' }}>
        {/* Project-type tasks with their subtasks */}
        {sortedProjects.map((project) => {
          const subs = subtasksByParent.get(project.id) ?? [];
          return (
            <div key={project.id} className="mb-3">
              <PlannerSidebarItem task={project} />
              {subs.map((sub) => (
                <PlannerSidebarItem key={sub.id} task={sub} isSubtask />
              ))}
            </div>
          );
        })}

        {/* Divider if both sections exist */}
        {sortedProjects.length > 0 && sortedRegularTasks.length > 0 && (
          <div className="border-t border-border-subtle/30 my-1" />
        )}

        {/* Regular (non-project) tasks with their subtasks */}
        {sortedRegularTasks.map((task) => {
          const subs = subtasksByParent.get(task.id) ?? [];
          return (
            <div key={task.id}>
              <PlannerSidebarItem task={task} />
              {subs.map((sub) => (
                <PlannerSidebarItem key={sub.id} task={sub} isSubtask />
              ))}
            </div>
          );
        })}

        {!hasContent && (
          <div className="px-3 py-4 text-[11px] text-text-muted text-center">
            No tasks on track
          </div>
        )}
      </div>
    </div>
  );
}
